import { useState } from 'react'
import { Button, Stack, Text } from '@mantine/core'
import { IconUsb } from '@tabler/icons-react'

import { MODELO_LABEL, type HardwareModel } from '@/api/robo'
import { PRAZOS_PADRAO, identificarRobo } from '@/features/robo-usb/fluxo'
import {
  eLeituraPerdida, motivoDaLeituraPerdida, versaoDoChrome, vigiarLeitura,
} from '@/features/robo-usb/leituraPerdida'
import type { InfoDoRobo } from '@/features/robo-usb/protocolo'
import { abrirLinhas, pedirPorta, temWebSerial } from '@/features/robo-usb/serial'

/** O que a placa respondeu, já com o perfil no tipo que o cadastro aceita. */
export type PlacaLida = InfoDoRobo & { perfil: HardwareModel }

type Estado = 'ocioso' | 'lendo' | 'antigo' | 'sem_resposta' | 'desconhecido' | 'erro' | 'caiu'

const esperar = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms))

const ehModeloConhecido = (perfil: string): perfil is HardwareModel => perfil in MODELO_LABEL

/**
 * Pergunta `INFO` à placa no cabo. É dela que vêm modelo e MAC: fato de
 * hardware, não escolha de quem cadastra (spec 2026-09-24-robo-sem-cliente, D6).
 *
 * Só entrega o que a placa AFIRMOU. Firmware antigo (sem `INFO`), robô calado
 * e perfil que este console não conhece viram frase — nunca um modelo chutado,
 * porque o modelo escolhe o binário que a gravação e o OTA entregam.
 */
export function LerRoboConectado({ onLido }: { onLido: (info: PlacaLida) => void }) {
  const [estado, setEstado] = useState<Estado>('ocioso')
  const [queda, setQueda] = useState('')

  if (!temWebSerial()) {
    return (
      <Text size="sm" c="dimmed">
        Ler a placa pela USB funciona no Chrome ou no Edge, no computador. Aqui, escolha o
        modelo à mão.
      </Text>
    )
  }

  const ler = async () => {
    // `pedirPorta` antes de qualquer outro await: o navegador só abre o
    // seletor de porta dentro do gesto do clique.
    let porta
    try {
      porta = await pedirPorta()
    } catch {
      return // fechou o seletor sem escolher
    }
    setEstado('lendo')
    // A leitura que o Chrome derruba chega ao leitor como linha nenhuma, igual
    // ao robô calado; só a porta vigiada sabe a diferença (TASK-11864).
    let perdida = false
    const caiu = () => {
      setQueda(motivoDaLeituraPerdida(porta.connected, versaoDoChrome()))
      setEstado('caiu')
    }
    let linhas
    try {
      linhas = await abrirLinhas(vigiarLeitura(porta, () => { perdida = true }))
    } catch {
      setEstado('erro')
      return
    }
    try {
      const r = await identificarRobo(linhas, PRAZOS_PADRAO, Date.now, esperar)
      if (r === undefined) {
        if (perdida) caiu()
        else setEstado('sem_resposta')
      }
      else if (r.info === null) setEstado('antigo')
      else if (!ehModeloConhecido(r.info.perfil)) setEstado('desconhecido')
      else {
        setEstado('ocioso')
        onLido({ ...r.info, perfil: r.info.perfil })
      }
    } catch (erro) {
      if (perdida || eLeituraPerdida(erro)) caiu()
      else setEstado('erro')
    } finally {
      await linhas.fechar()
    }
  }

  return (
    <Stack gap={4} align="flex-start">
      <Button
        variant="default"
        leftSection={<IconUsb size={16} />}
        loading={estado === 'lendo'}
        onClick={ler}
      >
        Ler robô conectado
      </Button>
      {estado === 'ocioso' && (
        <Text size="xs" c="dimmed">
          Com o robô no cabo USB, o Console lê o modelo e a placa direto dele.
        </Text>
      )}
      {estado === 'antigo' && (
        <Text size="sm">
          Este robô roda um firmware antigo, que não informa o modelo. Escolha o modelo à mão;
          depois de gravar o firmware novo, a placa passa a se identificar.
        </Text>
      )}
      {estado === 'sem_resposta' && (
        <Text size="sm">
          O robô não respondeu pela USB. Confira o cabo e se a porta escolhida é a dele.
        </Text>
      )}
      {estado === 'caiu' && <Text size="sm">{queda}</Text>}
      {estado === 'desconhecido' && (
        <Text size="sm">
          A placa informou um modelo que este console não conhece. Escolha o modelo à mão ou
          atualize o Console.
        </Text>
      )}
      {estado === 'erro' && (
        <Text size="sm">
          Não foi possível ler a porta. Feche outra página ou programa que esteja usando o robô
          e tente de novo.
        </Text>
      )}
    </Stack>
  )
}
